import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLine } from '../schemes/line/actions';

/**
 * Hook personalizado para gestionar automáticamente la carga de una línea
 * Si la línea no existe en el estado, la carga automáticamente usando idLine e idName
 * 
 * @param {string} idLine - ID de la línea
 * @param {string} idName - Nombre de la línea (opcional)
 * @param {Object} options - Opciones adicionales
 * @param {boolean} options.autoLoad - Si debe cargar automáticamente (default: true)
 * @param {boolean} options.forceReload - Si debe forzar la recarga aunque ya exista (default: false)
 * @returns {Object} - { line, loading, error, isLoaded, shouldLoadLine, reload }
 */
export const useLine = (idLine, idName, options = {}) => {
  const { 
    autoLoad = true, 
    forceReload = false 
  } = options;
  
  const dispatch = useDispatch();
  
  // Selectors del estado de la línea
  const lineState = useSelector((state) => state.line);
  const { active: line, loading, error } = lineState;
  
  // Verificar si la línea actual coincide con los parámetros solicitados
  const isCurrentLineMatching = useMemo(() => {
    if (!line || !idLine) return false;
    
    return (
      line.lineId === idLine || 
      line.id === idLine ||
      (line.slug === idName && idName) ||
      (line.uuid === idLine)
    );
  }, [line, idLine, idName]);
  
  // Determinar si necesitamos cargar la línea
  const shouldLoadLine = useMemo(() => {
    if (!idLine || !autoLoad) return false;
    if (loading) return false;
    if (forceReload) return true;
    return !isCurrentLineMatching;
  }, [idLine, autoLoad, loading, forceReload, isCurrentLineMatching]);
  
  // Función para recargar manualmente
  const reload = () => {
    if (idLine) {
      console.log('useLine: Manual reload for line:', idLine, idName);
      dispatch(getLine(idLine, idName));
    }
  };
  
  useEffect(() => {
    if (shouldLoadLine) {
      console.log('useLine: Auto-loading line with id:', idLine, 'name:', idName);
      dispatch(getLine(idLine, idName));
    }
  }, [dispatch, idLine, idName, shouldLoadLine]);

  return {
    line: isCurrentLineMatching ? line : null,
    loading,
    error,
    isLoaded: !!isCurrentLineMatching,
    shouldLoadLine,
    reload,
    // Estados adicionales útiles
    hasLine: !!line,
    lineId: line?.lineId || line?.id,
    lineName: line?.name,
    lineSlug: line?.slug
  };
};
